import { create } from 'zustand';
import { getActiveAnnouncement } from '../services/announcement';
import { useAuthStore } from './authStore';
import type { Announcement } from '../types';

const DISMISSED_PREFIX = 'splinity_announcement_dismissed_';

/**
 * Clé de stockage propre à l'utilisateur connecté.
 *
 * Le stockage est partagé par tout le navigateur : sans l'identifiant dans la
 * clé, fermer le bandeau sur un compte le masquerait aussi pour les autres
 * comptes ouverts depuis le même poste.
 */
function storageKey(): string | null {
  const userId = useAuthStore.getState().user?.id;
  return userId ? `${DISMISSED_PREFIX}${userId}` : null;
}

function loadDismissedVersion(): string | null {
  const key = storageKey();
  if (!key) return null;

  try {
    return localStorage.getItem(key);
  } catch {
    // Navigation privée, stockage bloqué… : le bandeau se réaffichera à chaque
    // visite, ce qui est préférable à un plantage au chargement.
    return null;
  }
}

function saveDismissedVersion(version: string) {
  const key = storageKey();
  if (!key) return;

  try {
    localStorage.setItem(key, version);
  } catch {
    /* stockage indisponible — la fermeture ne vaut alors que pour la session */
  }
}

interface AnnouncementState {
  announcement: Announcement | null;
  loaded: boolean;
  /** Version fermée par l'utilisateur, persistée. Une nouvelle version réaffiche le bandeau. */
  dismissedVersion: string | null;
  /**
   * Réouverture depuis le menu des notifications.
   *
   * Volontairement non persisté : l'aperçu ne survit ni à la fermeture du menu
   * ni à un rechargement — rouvrir une annonce n'est pas la « dé-fermer ».
   */
  previewing: boolean;

  fetchAnnouncement: () => Promise<void>;
  dismiss: () => void;
  preview: () => void;
  endPreview: () => void;
  clear: () => void;
}

export const useAnnouncementStore = create<AnnouncementState>((set, get) => ({
  announcement: null,
  loaded: false,
  dismissedVersion: null,
  previewing: false,

  fetchAnnouncement: async () => {
    try {
      const response = await getActiveAnnouncement();
      set({
        announcement: response.success ? response.data ?? null : null,
        // Relue ici plutôt qu'à la création du store : l'utilisateur n'est connu
        // qu'une fois connecté, et il peut changer sans rechargement de page.
        dismissedVersion: loadDismissedVersion(),
        loaded: true,
      });
    } catch {
      // Une annonce est un contenu d'agrément : son échec de chargement ne doit
      // rien signaler à l'utilisateur ni bloquer le reste de la page.
      set({ announcement: null, loaded: true });
    }
  },

  dismiss: () => {
    const { announcement } = get();
    if (announcement) {
      saveDismissedVersion(announcement.version);
      set({ dismissedVersion: announcement.version });
    }
    set({ previewing: false });
  },

  preview: () => set({ previewing: true }),
  endPreview: () => set({ previewing: false }),

  clear: () => set({ announcement: null, loaded: false, previewing: false }),
}));

/** Le bandeau s'affiche tant qu'il n'a pas été fermé — ou le temps d'un aperçu. */
export function useVisibleAnnouncement(): Announcement | null {
  return useAnnouncementStore((state) => {
    const { announcement, dismissedVersion, previewing } = state;
    if (!announcement) return null;
    if (previewing) return announcement;
    return dismissedVersion === announcement.version ? null : announcement;
  });
}

/** L'annonce rangée dans le menu des notifications (fermée, mais toujours active). */
export function useArchivedAnnouncement(): Announcement | null {
  return useAnnouncementStore((state) => {
    const { announcement, dismissedVersion } = state;
    if (!announcement) return null;
    return dismissedVersion === announcement.version ? announcement : null;
  });
}
